import { keepPreviousData, useQueries } from '@tanstack/react-query';
import { useMemo, useRef, useEffect } from 'react';
import { PRODUCT_QUERIES } from '@skin-seoul/service/src/infrastructure/product/utils/queries';
import type { RecommendedProduct, Currency } from '@skin-seoul/service';
import type { FilterType } from '../context';

type ProductRecommendationData = {
  original: RecommendedProduct;
  recommendations: RecommendedProduct[];
};

const isValidProduct = (product: RecommendedProduct | null | undefined): product is RecommendedProduct => {
  return product !== null && product !== undefined;
};

const buildRecommendationsMap = (queries: Array<{ data?: ProductRecommendationData }>, _productIds: number[]) => {
  const map = new Map<number, ProductRecommendationData>();
  queries.forEach((query) => {
    if (query.data) {
      const key = query.data.original.parentProductId ?? query.data.original.productId;
      map.set(key, query.data);
    }
  });
  return map;
};

type UseProductRecommendationsProps = {
  productIds: number[];
  currency: Currency;
  filter?: FilterType;
};

export const useProductRecommendations = ({ productIds, currency, filter }: UseProductRecommendationsProps) => {
  const hasLoadedOnceRef = useRef(false);
  const previousRecommendationsMapRef = useRef<Map<number, ProductRecommendationData>>(new Map());
  const previousRecentlyViewedProductsRef = useRef<RecommendedProduct[]>([]);

  const queries = useQueries({
    queries: productIds.map((id) => ({
      ...PRODUCT_QUERIES.getListRecommendations({
        productId: id,
        currency,
        filter,
      }),
      placeholderData: keepPreviousData,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    })),
  });

  const hasAllData = queries.length > 0 && queries.every((query) => query.data);
  const isFetching = queries.some((query) => query.isFetching);

  if (hasAllData) {
    hasLoadedOnceRef.current = true;
  }

  const currentRecommendationsMap = useMemo(() => buildRecommendationsMap(queries, productIds), [queries, productIds]);
  const currentRecentlyViewedProducts = useMemo(() => {
    return queries.map((query) => query.data?.original).filter(isValidProduct);
  }, [queries]);

  const recommendationsMap = hasAllData ? currentRecommendationsMap : previousRecommendationsMapRef.current;
  const recentlyViewedProducts = hasAllData ? currentRecentlyViewedProducts : previousRecentlyViewedProductsRef.current;

  useEffect(() => {
    if (hasAllData) {
      previousRecommendationsMapRef.current = currentRecommendationsMap;
      previousRecentlyViewedProductsRef.current = currentRecentlyViewedProducts;
    }
  }, [hasAllData, currentRecommendationsMap, currentRecentlyViewedProducts]);

  return { recommendationsMap, recentlyViewedProducts, isInitialLoading: !hasLoadedOnceRef.current, isFetching };
};
